import React, { useCallback, useState } from 'react'
import { Box, IconButton, Menu, MenuItem } from '@mui/material'
import { Dotsmenu } from '@e3dc-react/icons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ShowRoom } from 'src/Libs/company-settings/models/showroom.model'
import useModals from 'src/Hooks/useModals'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { useDispatch } from 'react-redux'

type Props = {
  showRoom: ShowRoom
  companyId: number
  setLoading: (isLoading: boolean) => void
  setError: (isError: boolean) => void
}

/**
 * Action dropdown menu for a company showroom
 */
const CompanyShowRoomsMenu: React.VFC<Props> = ({ showRoom, companyId, setLoading, setError }) => {
  const { t } = useCustomTranslation()
  const { openCompanyShowroomModal } = useModals()
  const authFetch = useAuthFetchWithResult()
  const dispatch = useDispatch()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const { openConfirmationModal } = useModals()

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const closeMenu = (): void => {
    setAnchorEl(null)
  }

  const handleClose = useCallback((): void => {
    closeMenu()
  }, [])

  const handleEdit = (): void => {
    openCompanyShowroomModal({
      entityAccess: 'edit',
      onSave: () => {
        dispatch(refreshApiView())
      },
      data: showRoom,
      companyId,
    })
  }

  const handleDelete = async (): Promise<void> => {
    setLoading(true)
    try {
      await authFetch({
        path: `users/companies/${companyId}/showrooms/${showRoom.id}`,
        method: 'DELETE',
      })
    } catch (error) {
      console.error(`Error while sending request to create a new showroom: ${error}`)
      setError(true)
    } finally {
      dispatch(refreshApiView())
      setLoading(false)
    }
  }

  const handleDeleteConfirmation = async (): Promise<void> => {
    openConfirmationModal({
      onConfirm: async onClose => {
        await handleDelete()
        onClose()
      },
      title: t('administration.companyManagement.showroom.delete-confirmation-title'),
      buttonText: [t('common.cancel'), t('common.delete')],
      text: t('administration.companyManagement.showroom.delete-confirmation-text', { name: showRoom.name }),
    })
  }

  return (
    <Box>
      <IconButton onClick={handleClick}>
        <Dotsmenu />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleEdit}>{t('administration.companyManagement.showroom.edit-action')}</MenuItem>
        <MenuItem onClick={handleDeleteConfirmation}>{t('administration.companyManagement.showroom.delete-action')}</MenuItem>
      </Menu>
    </Box>
  )
}

export default CompanyShowRoomsMenu
